import {
  Component,
  Listen,
  Prop,
  State,
  Watch,
  Element,
  Event,
  h,
  type EventEmitter,
} from '@stencil/core';
import { isMobile } from '../../../utils/media/media';
import {
  barItemsMessage,
  messageForAddedItem,
  messageForLastItem,
  messageForMobileCopmaringOneItem,
} from './uiaas-compare-bar-labels';
import { MessageSectionPartial } from './partials/compare-message';
import {
  storeGetter,
  storeKeySetter,
} from '../checkbox/uiaas-checkbox-helpers';
import {
  moveStickyItem,
  redirectionBuilder,
} from './uiaas-compare-bar-helpers';
import { CompareBarEventLayer } from './partials/compare-event-layer';
import { CompareComparisonPartial } from './partials/compare-comparison';
import { CompareCleanPartial } from './partials/compare-clean';
import type { labelProviderInterface } from '../../LabelProviderInterface';

@Component({
  tag: 'uiaas-compare-bar',
  styleUrl: 'uiaas-compare-bar.scss',
  shadow: true,
})
export class CompareBarComponent {
  @Prop() readonly labelProvider: labelProviderInterface;
  @Prop() readonly country: string;
  @Prop() readonly language: string;
  @Prop() readonly term: string;
  @Prop() readonly redirectionBaseUrl: string;
  @Prop() readonly stickyScrollTopBottomValue?: number;
  @Prop() readonly stickyElementsBottomOffset?: number;

  @State() isCheckboxActionInvoked = false;
  @State() compareStorageValues = [];
  @State() isMessageVisibleForAddAction = false;
  @State() isMessageVisibleForMaximumItem = false;
  @State() isMessageVisibleForOneItem = false;
  @State() isProductRelatedTab = true;
  @State() isMobileView = isMobile();

  @Element() element;

  private timeoutId = null;

  /**
  * Trigger floating elements postioning logic before mobile breakpoint
  */
  private breakpoint = 1080;

  @Listen('comparison:maximum-item-on-disabled-action', { target: 'document' })
  handleMaximumItemMessageForDisabledCheckbox() {
      this.isMessageVisibleForMaximumItem = true;
  }

  @Listen('comparison:local-storage-action', { target: 'document' })
  handleComparisonValueChange() {
      this.isCheckboxActionInvoked = !this.isCheckboxActionInvoked; // Force rerender comparison bar after checkbox action
  }

  @Listen('resize', { target: 'window' })
  handleResize() {
      this.isMobileView = isMobile();

      moveStickyItem(
        this.element,
        this.compareStorageValues?.length,
        this.isMobileView,
        this.isBreakPointActive(),
        this.stickyElementsBottomOffset,
        this.stickyScrollTopBottomValue

      );
  }

  @Listen('search:tabClick', { target: 'window' })
  handleActiveTabChange(e: CustomEvent) {
      const detail = e.detail;
      const isProductsRelatedTab =
      detail.tabName.includes('products') || detail.tabName.includes('all');

      isProductsRelatedTab
        ? (this.isProductRelatedTab = true)
        : (this.isProductRelatedTab = false);
  }

  @Listen('compare:messageCloseAction', { target: 'document' })
  handleCloseMessageAction(
    e: CustomEvent<{
      isInformative: boolean;
    }>,
  ) {
      const detail = e.detail;

      if (detail.isInformative) {
        this.isMessageVisibleForOneItem = false;
        this.isMessageVisibleForMaximumItem = false;
      } else {
        this.isMessageVisibleForAddAction = false;
      }
  }

  @Event({
    eventName: 'compare:compareBarAction',
  })
    compareBarActionEventEmitter: EventEmitter;

  @Event({
    eventName: 'search:SearchResultClickEvent',
  })
    searchResultClickEventEmitter: EventEmitter;

  @Watch('compareStorageValues')
  async handleArrayChange(newArray: string[], oldArray: string[]) {
      if (!newArray.length && !this.isCheckboxActionInvoked) return;
      if (newArray.length < 4) {
        this.isMessageVisibleForMaximumItem = false;
      }

      if (newArray.length > oldArray.length) {
        if (newArray.length >= 2) this.isMessageVisibleForOneItem = false;
        if (newArray.length === 4) {
          await this.clearTimeout();
          this.isMessageVisibleForMaximumItem = true;
          this.startTimeout();
        }

        if (newArray.length < 4) {
          await this.clearTimeout();
          this.isMessageVisibleForAddAction = true;
          this.startTimeout();
        }
      }
  }

  /**
  * Trigger breakpoint only for product related Tabs.
  * For non product related, view is different so breakpoint should not be returned
  */
  private isBreakPointActive = () => {
      if (this.isProductRelatedTab) return this.breakpoint;
      return;
  };

  private resetMessages = () => {
      this.isMessageVisibleForOneItem = false;
      this.isMessageVisibleForAddAction = false;
      this.isMessageVisibleForMaximumItem = false;
  };

  private startTimeout() {
      this.timeoutId = setTimeout(() => {
          this.isMessageVisibleForAddAction = false;
          this.isMessageVisibleForMaximumItem = false;
      }, 5000);
  }

  private clearTimeout() {
      this.isMessageVisibleForAddAction = false;
      this.isMessageVisibleForMaximumItem = false;
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
  }

  private redirectionSetter = () =>
    redirectionBuilder(this.compareStorageValues, this.country, this.language, this.redirectionBaseUrl);
  private compareOneItemActionHandlerForSmallVariant = () =>
    this.isMobileView && this.compareStorageValues.length === 1
      ? (this.isMessageVisibleForOneItem = true)
      : this.redirectionSetter();
  private renderCorrectMessageVariant = () => {
      const itemAddedLabel = this.labelProvider(
        messageForAddedItem.label,
        messageForAddedItem.fallback,
      );
      const itemMaximumLabel = this.labelProvider(
        messageForLastItem.label,
        messageForLastItem.fallback,
      );
      const itemComparingOneLabel = this.labelProvider(
        messageForMobileCopmaringOneItem.label,
        messageForMobileCopmaringOneItem.fallback,
      );

      return (
        <div>
          {this.isMessageVisibleForAddAction ? (
            <MessageSectionPartial text={itemAddedLabel} color="blue" />
          ) : (
            ''
          )}
          {this.isMessageVisibleForMaximumItem ? (
            <MessageSectionPartial text={itemMaximumLabel} color="red" />
          ) : (
            ''
          )}
          {this.isMessageVisibleForOneItem && this.isMobileView ? (
            <MessageSectionPartial
              text={itemComparingOneLabel}
              color="blue-informative"
            />
          ) : (
            ''
          )}
        </div>
      );
  };

  componentDidLoad() {
      this.resetMessages();
  }

  componentDidRender() {
      moveStickyItem(
        this.element,
        this.compareStorageValues?.length,
        this.isMobileView,
        this.isBreakPointActive(),
        this.stickyElementsBottomOffset,
        this.stickyScrollTopBottomValue
      );
  }

  render() {
      const storageKey = storeKeySetter(this.country, this.language);

      this.compareStorageValues = storeGetter(storageKey);

      const idsArrayLength = this.compareStorageValues?.length;
      const isMobileRedirectionTurnedOn = this.isMobileView && idsArrayLength > 1;
      const compareActionHandlerForSmallVariant = isMobileRedirectionTurnedOn
        ? this.redirectionSetter()
        : void 0;

      if (!idsArrayLength) this.isMessageVisibleForOneItem = false;

      return idsArrayLength ? (
        <CompareBarEventLayer
          IDs={this.compareStorageValues?.join('&')}
          eventEmitter={this.compareBarActionEventEmitter}
          resultClickEventEmitter={this.searchResultClickEventEmitter}
          redirectionUrl={this.redirectionSetter()}
          siteSearchKeyword={this.term}
        >
          <div
            class={{
              'compare-wrapper': true,
              'compare-wrapper--not-product-related-tab':
              !this.isProductRelatedTab,
            }}
          >
            {this.isProductRelatedTab || this.isMobileView
              ? this.renderCorrectMessageVariant()
              : ''}
            <a
              class="compare-wrapper-bar"
              href={compareActionHandlerForSmallVariant}
              target="_blank"
              tabIndex={this.isMobileView ? 0 : void 0}
              data-compare-area={
                isMobileRedirectionTurnedOn ? 'compare_now' : void 0
              }
              onClick={() => {
                  this.compareOneItemActionHandlerForSmallVariant();
              }}
              onKeyDown={(e) =>
                e.key === 'Enter' ? this.compareOneItemActionHandlerForSmallVariant() : void 0
              }
            >
              <div class="compare-wrapper-bar-information">
                <section>
                  <span class="compare-wrapper-bar-information__text">
                    {this.labelProvider(
                      barItemsMessage.label,
                      barItemsMessage.fallback,
                    )}
                  </span>
                  <span class="compare-wrapper-bar-information__counter">
                    {idsArrayLength}/4
                  </span>
                </section>
                <CompareCleanPartial
                  labelProvider={(e, f) => this.labelProvider(e, f)}
                  storageKey={storageKey}
                  isMobile={this.isMobileView}
                  isProductRelatedTab={this.isProductRelatedTab}
                />
              </div>
              <CompareComparisonPartial
                numberOfSelectedItems={idsArrayLength}
                redirectionUrl={this.redirectionSetter()}
                labelProvider={(e, f) => this.labelProvider(e, f)}
              />
            </a>
          </div>
        </CompareBarEventLayer>
      ) : (
        ''
      );
  }
}
